import { useEffect, useRef, type RefObject } from 'react';

/**
 * usePaulinaDialogA11y — accesibilidad compartida del chat de Paulina como diálogo
 * modal (panel desktop y bottom-sheet móvil). Modela QuickPresentationsSheet:
 *
 *   1. Escape cierra el diálogo.
 *   2. Retorno de foco al activador al desmontar (capturado al montar, no en cada
 *      render, para no atrapar refocos posteriores).
 *   3. Focus-trap: Tab/Shift+Tab circula SOLO entre los focusables del diálogo.
 *      Al montar mueve el foco al primer focusable (entrada al modal).
 *
 * Devuelve el `ref` que el contenedor del diálogo (el nodo con role=dialog) debe
 * recibir. El consumidor sigue siendo dueño de role/aria-modal/aria-label.
 */

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export const usePaulinaDialogA11y = (onClose: () => void): RefObject<HTMLDivElement> => {
  const dialogRef = useRef<HTMLDivElement>(null);
  // Ref siempre-actual del callback: el caller puede pasar un onClose sin memoizar,
  // así el listener de Escape NO se re-suscribe en cada render (lo reportó Seer).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Escape cierra (paridad con el molde de sheets v4). Suscripción estable (deps []).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Retorno de foco al activador al cerrar: capturamos el elemento activo al montar.
  const previouslyFocused = useRef<HTMLElement | null>(null);
  useEffect(() => {
    previouslyFocused.current = (document.activeElement as HTMLElement | null) ?? null;
    return () => {
      previouslyFocused.current?.focus?.();
    };
  }, []);

  // Focus-trap: confina Tab/Shift+Tab a los focusables del diálogo y entra el foco.
  useEffect(() => {
    const node = dialogRef.current;
    if (!node) return;

    const focusables = (): HTMLElement[] =>
      Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      );

    // Entrada al modal: enfoca el primer focusable (no roba foco si ya está dentro).
    if (!node.contains(document.activeElement)) focusables()[0]?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const items = focusables();
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey) {
        if (active === first || !node.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last || !node.contains(active)) {
        e.preventDefault();
        first.focus();
      }
    };

    node.addEventListener('keydown', onKeyDown);
    return () => node.removeEventListener('keydown', onKeyDown);
  }, []);

  return dialogRef;
};
